import Analytics from 'modules/Analytics/Analytics';
import END_REASON from 'modules/Analytics/constants/endReason';
import MoreLink from 'widget/components/MoreLink';
import PLAYED_FROM from 'modules/Analytics/constants/playedFrom';
import Player from 'widget/contexts/Player';
import PlayerState from 'modules/Player/constants/playerState';
import Row from 'widget/components/Row';
import Rows from 'widget/components/Rows';
import SubText from 'widget/components/SubText';
import Text from 'widget/components/Text';
import useMount from 'hooks/useMount';
import useTrackers from 'widget/contexts/Trackers/hooks/useTrackers';
import { Episode } from 'state/Podcast/types';
import { Events } from 'widget/trackers/types';
import { formatDateShort, formatEpisodeDuration } from 'utils/time/dates';
import {
  generatePodcastPlaybackTrackingData,
  getPodcastPlaybackAnalyticsPayload,
} from 'widget/Podcast/helpers/analytics';
import { useContext, useEffect, useRef, useState } from 'react';
import { usePlayerProgress, useTranslate } from 'widget/hooks';

type Props = {
  analytics?: Analytics;
  episodes: Array<Episode>;
  onClick: () => void;
  podcastId: string;
  podcastUrl?: string;
  title: string;
};

function Episodes({
  analytics,
  episodes,
  onClick,
  podcastId,
  podcastUrl,
  title,
}: Props) {
  const trackers = useTrackers();
  const translate = useTranslate();

  const {
    getCurrentlyPlaying,
    play: onClickPlay,
    playerState,
  } = useContext(Player.Context);
  const [progress] = usePlayerProgress();
  const isPlaying = playerState === PlayerState.Playing;
  const { trackId: currentlyPlayingId } = getCurrentlyPlaying() || {};
  const [innerHeightRef, setInnerHeightRef] = useState(0);
  const [streamInitTime, setStreamInitTime] = useState<number>(0);
  const trackingDataRef = useRef(
    generatePodcastPlaybackTrackingData({
      episodeId: '',
      name: title,
      playedFrom: PLAYED_FROM.RESP_WIDGET_PROFILE_EPISODE_PLAY,
      podcastId,
      subName: '',
    }),
  );

  const trackerData = {
    id: currentlyPlayingId,
    name: title,
    type: 'podcast',
    typeId: podcastId,
  };

  const play = (episodeId: string, episodeTitle: string) => {
    const currentEpisode = episodes.find(e => `${e.id}` === currentlyPlayingId);
    const currentTrackingData = generatePodcastPlaybackTrackingData({
      episodeId: currentlyPlayingId || '',
      name: title,
      playedFrom: PLAYED_FROM.RESP_WIDGET_PROFILE_EPISODE_PLAY,
      podcastId,
      subName: currentEpisode?.title ?? '',
    });
    if (isPlaying && episodeId === currentlyPlayingId) {
      trackers.track(Events.Pause);
      analytics?.track(Events.Pause, currentTrackingData);
      analytics?.track(
        Events.StreamEnd,
        getPodcastPlaybackAnalyticsPayload(currentTrackingData, {
          endReason: END_REASON.PAUSE,
          listenTime: Math.round(progress?.position ?? 0),
        }),
      );
    } else {
      trackingDataRef.current = generatePodcastPlaybackTrackingData({
        episodeId,
        name: title,
        playedFrom: PLAYED_FROM.RESP_WIDGET_PROFILE_EPISODE_PLAY,
        podcastId,
        subName: episodeTitle,
      });
      analytics?.track(Events.Play, trackingDataRef.current);
      setStreamInitTime(Date.now());
      trackers.track(Events.Play, trackerData);
      if (isPlaying) {
        analytics?.track(
          Events.StreamEnd,
          getPodcastPlaybackAnalyticsPayload(currentTrackingData, {
            endReason: END_REASON.NEW_STATION_START,
            listenTime: Math.round(progress?.position ?? 0),
          }),
        );
      }
    }
    onClick();
    onClickPlay(podcastId, { trackId: episodeId });
  };

  useMount(() => {
    setInnerHeightRef(window?.innerHeight);
  });

  useEffect(() => {
    if (playerState === PlayerState.Playing && streamInitTime) {
      analytics?.track(
        Events.StreamStart,
        getPodcastPlaybackAnalyticsPayload(trackingDataRef.current, {
          playbackStartTime: Date.now(),
          streamInitTime,
        }),
      );
      setStreamInitTime(0);
      trackers.track(Events.StreamStart, trackerData);
    }
  }, [playerState, streamInitTime]);

  const KEYBOARD_SELECT_KEYS = ['Space', ' ', 'Enter'];
  return (
    <Rows height={innerHeightRef}>
      {episodes.map(({ duration, id, startDate, title: episodeTitle }) => (
        <Row
          data-test="podcast-episode-row"
          key={id}
          onClick={() => play(`${id}`, episodeTitle)}
          onKeyUp={e => {
            if (KEYBOARD_SELECT_KEYS.includes(e.key))
              play(`${id}`, episodeTitle);
          }}
          tabIndex={0}
        >
          <Text
            data-test="episode-title"
            selected={`${id}` === currentlyPlayingId}
          >
            {episodeTitle}
          </Text>
          <SubText data-test="subtext">
            {formatDateShort(startDate, translate)} •{' '}
            {formatEpisodeDuration(duration)}
          </SubText>
        </Row>
      ))}
      <MoreLink
        href={podcastUrl || ''}
        text={translate('Listen to more episodes')}
      />
    </Rows>
  );
}

export default Episodes;
